import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiAxios } from "../../../../utils/apiAxios";
import { ManagerOrderRow } from "../../../../Interfaces/MangerOrderRow";
import { ManagerOrderReviewRow } from "../../../../Interfaces/ManagerOrderReviewRow";
import React from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// Define the ModalTab type
type ModalTab = "order-status" | "order-review" | "actions";

interface UseOrderModalReturn {
  activeModalTab: ModalTab;
  orderDetails: any;
  detailsLoading: boolean;
  orderReview: ManagerOrderReviewRow[];
  isOrderModalOpen: boolean;
  selectedOrder: ManagerOrderRow | null;
  cancelReason: string;
  setCancelReason: (reason: string) => void;
  closeModal: () => void;
  handleTabClick: (tab: ModalTab) => void;
  openOrderModal: (order: ManagerOrderRow) => void;
  reassignDesigner: (
    caseId: number,
    designerId: number
  ) => Promise<boolean>;
  reassignMilling: (
    caseId: number,
    millingId: number
  ) => Promise<boolean>;
   reassignDelivery: (
    caseId: number,
    deliveryPartnerId: number
  ) => Promise<boolean>;
 deliveryPartners: any[];
  designers: any[];
  millings: any[];
  cancelOrder: (caseId: number, reason: string) => Promise<boolean>;
  closeModalAfterAction: () => void;
}

export const useOrderModal = (): UseOrderModalReturn => {
  const [designers, setDesigners] = useState<any[]>([]);
  const [millings, setMillings] = useState<any[]>([]);
  const [deliveryPartners, setDeliveryPartners] = useState<any[]>([]);

  const [activeModalTab, setActiveModalTab] = useState<ModalTab>("order-status");
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [orderReview, setOrderReview] = useState<ManagerOrderReviewRow[]>([]);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ManagerOrderRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  const fetchDesigners = useCallback(async () => {
    try {
      const res = await apiAxios.get(
        `${API_BASE}/Central/designer-dropdown`
      );

      if (res.data?.isSuccess) {
        setDesigners(res.data.data || [],);
      }
    } catch {
      toast.error("Failed to load designers");
    }
  }, []);

  const fetchMillings = useCallback(async () => {
    try {
      const res = await apiAxios.get(
        `${API_BASE}/Central/milling-dropdown`
      );

      if (res.data?.isSuccess) {
        setMillings(res.data.data || []);
      }
    } catch {
      toast.error("Failed to load milling hubs");
    }
  }, []);
  const fetchDeliveryPartners = useCallback(async () => {
  
  try {
    const res = await apiAxios.get(
      `${API_BASE}/Central/delivery-dropdown`
    );

      
    if (res.data?.isSuccess) {
      setDeliveryPartners(res.data.data || []);
       console.log(res.data.data,"***********");
    }
  } catch {
    toast.error("Failed to load delivery partners");
  }
}, []);


  // Create a ref to track if we should close modal after action
  const shouldCloseModalRef = useRef(true);

  const closeModal = useCallback((): void => {
    setIsOrderModalOpen(false);
    setSelectedOrder(null);
    setOrderDetails(null);
    setCancelReason("");
    setActiveModalTab("order-status");
  }, []);

  const closeModalAfterAction = useCallback((): void => {
    if (shouldCloseModalRef.current) {
      closeModal();
    }
  }, [closeModal]);

  const fetchOrderDetails = useCallback(async (orderId: string): Promise<void> => {
    try {
      setDetailsLoading(true);
      setActiveModalTab("order-status");

      const res = await apiAxios.get(`${API_BASE}/Central/case-order-status`, {
        params: { orderID: orderId }
      });
      
      if (res.data?.isSuccess && res.data?.data?.length > 0) {
        setOrderDetails(res.data.data[0]);
        console.log(res.data.data[0],"orderdetails**");
        setIsOrderModalOpen(true);
      }
    } catch {
      toast.error("Failed to load order details");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const fetchOrderReviewByCaseId = useCallback(async (caseId: number): Promise<void> => {
    try {
      setDetailsLoading(true);
      const res = await apiAxios.get(`${API_BASE}/Central/case-order-review`, {
        params: { caseID: caseId }
      });
      if (res.data?.isSuccess) {
        setOrderReview(res.data.data || []);
      }
    } catch {
      toast.error("Failed to load order review");
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const reassignDesigner = useCallback(
    async (
      caseId: number,
      designerId: number
    ): Promise<boolean> => {
      try {
        const res = await apiAxios.post(`${API_BASE}/Central/reassign-designer`, {
          caseId,
          designerId,
        });

        if (res.data?.isSuccess) {
          toast.success("Designer reassigned successfully");
          closeModalAfterAction();
          return true; // Return success
        } else {
          toast.error(res.data?.status || "Failed to reassign designer");
          return false;
        }
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.status ||   // your API response.Status
          err?.response?.data?.errors?.[0] || // optional errors array
          err?.message ||
          "Error reassigning designer";

        toast.error(errorMessage);

        return false;
      }
    }, [closeModalAfterAction]);

  const reassignMilling = useCallback(
    async (
      caseId: number,
      millingId: number
    ): Promise<boolean> => {
      try {
        const res = await apiAxios.post(`${API_BASE}/Central/reassign-milling`, { caseId, millingId });

        if (res.data?.isSuccess) {
          toast.success(res.data?.status || "Milling hub reassigned successfully");
          closeModalAfterAction();
          return true;
        } else {
          toast.error(res.data?.status || "Failed to reassign milling hub");
          return false;
        }

      } catch (err: any) {
        // ✅ Handle backend error message properly
        const errorMessage =
          err?.response?.data?.errors?.[0] ||   // from your API
          err?.response?.data?.status ||       // fallback
          err?.message ||                      // generic
          "Error reassigning milling hub";

        toast.error(errorMessage);
        return false;
      }
    }, [closeModalAfterAction]);
    

  const reassignDelivery = useCallback(
  async (
    caseId: number,
    deliveryPartnerId: number
  ): Promise<boolean> => {
    try {
      const res = await apiAxios.post(
        `${API_BASE}/Central/reassign-delivery`,
        {
          caseId,
          deliveryPartnerId,
        }
      );

      if (res.data?.isSuccess) {
        toast.success(
          res.data?.status || "Delivery partner reassigned successfully"
        );

        closeModalAfterAction();
        return true;
      } else {
        toast.error(
          res.data?.status || "Failed to reassign delivery partner"
        );
        return false;
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.status ||
        err?.message ||
        "Error reassigning delivery partner";

      toast.error(errorMessage);
      return false;
    }
  },
  [closeModalAfterAction]
);

  const cancelOrder = useCallback(async (caseId: number, reason: string): Promise<boolean> => {
    try {
      const res = await apiAxios.post(`${API_BASE}/Central/CancelOrder`, {
        caseId,
        cancellationReason: reason
      });

      if (res.data?.isSuccess) {
        //toast.success("Order cancelled successfully");
        setCancelReason("");
        closeModalAfterAction();
        return true;
      } else {
        toast.error(res.data?.status || "Failed to cancel order");
        return false;
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.status || "Error cancelling order");
      return false;
    }
  }, [closeModalAfterAction]);

  const handleTabClick = useCallback((tab: ModalTab): void => {
    setActiveModalTab(tab);
    if (tab === "order-review" && selectedCaseId) {
      fetchOrderReviewByCaseId(selectedCaseId);
    }
  }, [selectedCaseId, fetchOrderReviewByCaseId]);

  const openOrderModal = useCallback((order: ManagerOrderRow) => {
    shouldCloseModalRef.current = true;

    setSelectedCaseId(order.caseID);
    setSelectedOrder(order);

    fetchOrderDetails(order.orderId);

    fetchDesigners();
    fetchMillings();
    fetchDeliveryPartners();

  }, [
    fetchOrderDetails,
    fetchDesigners,
    fetchMillings,
    fetchDeliveryPartners
  ]);

  // Optional: Function to prevent modal close in certain cases
  const preventModalClose = useCallback(() => {
    shouldCloseModalRef.current = false;
  }, []);

  // Optional: Function to allow modal close
  const allowModalClose = useCallback(() => {
    shouldCloseModalRef.current = true;
  }, []);

  return {
    activeModalTab,
    orderDetails,
    detailsLoading,
    orderReview,
    isOrderModalOpen,
    selectedOrder,
    cancelReason,
    setCancelReason,
    closeModal,
    handleTabClick,
    openOrderModal,
    reassignDesigner,
    reassignMilling,
     reassignDelivery,
    cancelOrder,
    designers,
    millings,
    deliveryPartners,
    closeModalAfterAction
  };
};


// Export the type for use in other components
export type { ModalTab };